// src/pages/merchant/Profile.js
// Marchand : profil personnel (infos + photo + mot de passe)
// Maxula

import React, { useEffect, useState, useRef } from 'react';
import { FiSave, FiLock, FiUpload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { userAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import { useAuth } from '../../context/AuthContext';

const MerchantProfile = () => {
  const { user, refreshUser } = useAuth() || {};
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [savingMedia, setSavingMedia] = useState(false);
  const avatarRef = useRef(); const coverRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const res = await userAPI.getProfile();
        const u = res.data?.data || res.data?.user || res.data || {};
        setForm({
          firstName: u.firstName || '', lastName: u.lastName || '',
          email: u.email || '', phone: u.phone || ''
        });
        if (u.avatar && !String(u.avatar).includes('avatar-default')) setAvatarPreview(normalizeImagePath(u.avatar));
        if (u.coverImage) setCoverPreview(normalizeImagePath(u.coverImage));
      } catch (e) {
        if (user) setForm({ firstName: user.firstName || '', lastName: user.lastName || '', email: user.email || '', phone: user.phone || '' });
      }
    })();
  }, [user]);

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const pickAvatar = (e) => { const f = e.target.files?.[0]; if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); } };
  const pickCover = (e) => { const f = e.target.files?.[0]; if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); } };
  const saveMedia = async () => {
    if (!avatarFile && !coverFile) return toast.error('Choisissez une image');
    try {
      setSavingMedia(true);
      const fd = new FormData();
      if (avatarFile) fd.append('avatar', avatarFile);
      if (coverFile) fd.append('coverImage', coverFile);
      const res = await userAPI.updateMedia(fd);
      const d = res.data?.data;
      if (d?.avatar) setAvatarPreview(normalizeImagePath(d.avatar));
      if (d?.coverImage) setCoverPreview(normalizeImagePath(d.coverImage));
      setAvatarFile(null); setCoverFile(null);
      toast.success('Photos mises à jour');
      if (typeof refreshUser === 'function') refreshUser();
    } catch (e) { toast.error('Erreur lors de l\'envoi'); }
    finally { setSavingMedia(false); }
  };

  const save = async () => {
    try {
      setSaving(true);
      await userAPI.updateProfile(form);
      toast.success('Profil mis à jour');
      if (typeof refreshUser === 'function') refreshUser();
    } catch (e) { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (pwd.newPassword.length < 8) return toast.error('8 caractères minimum');
    if (pwd.newPassword !== pwd.confirm) return toast.error('Les mots de passe ne correspondent pas');
    try {
      setSavingPwd(true);
      await userAPI.updatePassword({ currentPassword: pwd.currentPassword, newPassword: pwd.newPassword });
      toast.success('Mot de passe modifié');
      setPwd({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (e) { toast.error(e?.response?.data?.message || 'Mot de passe actuel incorrect'); }
    finally { setSavingPwd(false); }
  };

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Mon profil</h1>

      {/* Photo de profil + couverture */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">Photo & couverture</h2>
        <div className="relative h-28 rounded-xl bg-gray-100 overflow-hidden mb-3">
          {coverPreview && <img src={coverPreview} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover" />}
          <button onClick={() => coverRef.current?.click()} className="absolute bottom-2 right-2 bg-white/90 px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 hover:bg-white"><FiUpload size={14} /> Couverture</button>
          <input ref={coverRef} type="file" accept="image/*" onChange={pickCover} className="hidden" />
        </div>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center">
            {avatarPreview ? <img src={avatarPreview} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover" /> : <span className="text-2xl text-gray-300">{(form.firstName || 'A').charAt(0)}</span>}
          </div>
          <button onClick={() => avatarRef.current?.click()} className="px-3 py-2 rounded-lg border text-sm flex items-center gap-1 hover:bg-gray-50"><FiUpload size={14} /> Changer la photo</button>
          <input ref={avatarRef} type="file" accept="image/*" onChange={pickAvatar} className="hidden" />
          <button onClick={saveMedia} disabled={savingMedia} className="ml-auto px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60">{savingMedia ? '...' : 'Enregistrer les photos'}</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="font-semibold mb-4">Informations</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input name="firstName" value={form.firstName} onChange={onField} placeholder="Prénom" className="border rounded-lg px-3 py-2" />
          <input name="lastName" value={form.lastName} onChange={onField} placeholder="Nom" className="border rounded-lg px-3 py-2" />
          <input name="email" value={form.email} onChange={onField} placeholder="Email" className="border rounded-lg px-3 py-2" />
          <input name="phone" value={form.phone} onChange={onField} placeholder="Téléphone" className="border rounded-lg px-3 py-2" />
        </div>
        <button onClick={save} disabled={saving}
          className="mt-4 px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60">
          <FiSave /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="font-semibold mb-4 flex items-center gap-2"><FiLock /> Changer le mot de passe</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input type="password" placeholder="Actuel" value={pwd.currentPassword} onChange={(e) => setPwd((p) => ({ ...p, currentPassword: e.target.value }))} className="border rounded-lg px-3 py-2" />
          <input type="password" placeholder="Nouveau (min. 8)" value={pwd.newPassword} onChange={(e) => setPwd((p) => ({ ...p, newPassword: e.target.value }))} className="border rounded-lg px-3 py-2" />
          <input type="password" placeholder="Confirmer" value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} className="border rounded-lg px-3 py-2" />
        </div>
        <button onClick={savePassword} disabled={savingPwd}
          className="mt-4 px-5 py-2.5 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50 disabled:opacity-60">
          {savingPwd ? 'Modification…' : 'Modifier le mot de passe'}
        </button>
      </div>
    </div>
  );
};

export default MerchantProfile;
